import { useEffect, useState, type CSSProperties } from "react";
import { useSession } from "@core/session/SessionProvider";
import { Can } from "@core/permissions";
import { Icon } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { formatShortDate } from "@core/format/shortDate";
import type {
  SchedulingException,
  SchedulingHoursDay,
  SchedulingHoursDayInput,
  SchedulingQuotaHasAppointmentsProblem,
} from "@core/types";
import { ApiError } from "@core/http";
import {
  DialogActions,
  DialogHeader,
  Field,
  FieldError,
  Modal,
  PortalButton,
  useToast,
} from "@portal/common";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import {
  useSchedulingExceptions,
  useSchedulingMutations,
  useWorkshopHours,
  useWorkshopQuotas,
  useWorkshops,
} from "./schedulingQueries";
import {
  exceptionFormSchema,
  type ExceptionFormValues,
} from "./schedulingSchemas";
import {
  formatHoursEntry,
  parseHoursEntry,
  type HoursParseResult,
} from "./hoursFormat";

// admin.jsx:91-92 (Card): fondo/borde/radio comunes de toda tarjeta de esta
// pantalla — el prototipo lo resuelve con un componente propio; acá se
// reutiliza como un objeto de estilo porque no hay un `Card` compartido en
// `@portal/common` (mismo criterio que `home-content`, que también arma sus
// tarjetas con `tokens.surface.*` directo en vez de una clase CSS `.card`,
// que no existe en base.css).
function cardStyle(pad = 22): CSSProperties {
  return {
    background: tokens.surface.surface,
    border: `1px solid ${tokens.surface.line}`,
    borderRadius: 18,
    padding: pad,
  };
}

// admin.jsx DIAS: día 0 = domingo (api/db/001-initial-schema.sql:1011), mismo
// valor que consume el contrato (SchedulingHoursDay.day) y el `check between
// 0 and 6` del backend — eso NUNCA cambia. `DAY_LABELS` está indexado por
// ese valor; `DAY_ORDER` (PLAN-009 §7.1) es solo el orden de PRESENTACIÓN
// lunes→domingo (admin.jsx:69 DIAS), tres letras (`label.slice(0,3)`).
const DAY_LABELS: Record<number, string> = {
  0: "Domingo",
  1: "Lunes",
  2: "Martes",
  3: "Miércoles",
  4: "Jueves",
  5: "Viernes",
  6: "Sábado",
};
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

type HoursDraft = Record<number, string>;
type HoursErrors = Record<number, string | undefined>;
type ExceptionFieldErrors = {
  onDate?: string | undefined;
  slotTime?: string | undefined;
  reason?: string | undefined;
};

// SPEC-207 §4.1/§5 (P8A-01, resolución humana): el 409 QUOTA_HAS_APPOINTMENTS
// va siempre al toast, con el conteo y los miembros afectados — nunca a un
// estado dentro del formulario.
function quotaConflictMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "QUOTA_HAS_APPOINTMENTS")
    return null;
  const problem = error.problem as
    SchedulingQuotaHasAppointmentsProblem | undefined;
  const impact = problem?.impact;
  if (!impact) return null;
  const names = impact.appointments.map((a) => a.memberName).join(", ");
  return `Hay ${String(impact.count)} cita(s) activa(s) que impiden esta acción: ${names}`;
}

// SchedulingService.createException (P8-2, campo `reason` obligatorio) y
// SchedulingService.replaceHours (campo `hours`) arman `fieldRejection`
// (`api/src/lib/result.ts`): el mismo `INVALID_REQUEST` que `submitAddSlot`/
// `submitException` ya mapean a mano para la franja/excepción duplicada
// (`uq_quota`/`uq_exception`). Sin este chequeo, el mapeo manual de duplicado
// ganaría siempre y taparía el motivo real que la API sí identificó por
// campo — se prueba primero, y solo si no hay campo esa unión cae al mapeo
// de siempre.
function apiFieldErrorMessage(error: unknown, field: string): string | null {
  if (!(error instanceof ApiError)) return null;
  return (
    error.problem?.errors?.find((item) => item.field === field)?.message ?? null
  );
}

// Mismo texto que la API (`uq_exception`, scheduling.repository.ts).
const DUPLICATE_EXCEPTION_MESSAGE =
  "Ya existe una excepción registrada para esa fecha y franja";
const EXCEPTION_FIELDS = ["onDate", "slotTime", "reason"] as const;

// Decisión 2026-09-29: la API rechaza también una segunda excepción de día
// completo en la misma fecha (antes `slot_time` nulo escapaba a
// `uq_exception`). Se replica antes de enviar contra la lista que la pantalla
// ya tiene cargada: misma fecha y mismo alcance (día completo o la misma
// franja) que una excepción vigente.
function isDuplicateException(
  existing: readonly SchedulingException[] | undefined,
  candidate: ExceptionFormValues,
): boolean {
  const slot = candidate.slotTime.slice(0, 5);
  return (existing ?? []).some(
    (exception) =>
      exception.onDate.slice(0, 10) === candidate.onDate &&
      (exception.slotTime ?? "").slice(0, 5) === slot,
  );
}

// Findings ola 2 (design-reviewer, PLAN-009 §7.6): `submitException` no
// mapeaba INVALID_REQUEST al mensaje de dominio de la excepción duplicada
// (scheduling.repository.ts:288-291, insertException catch), a diferencia
// de `submitAddSlot`, que ya mapea INVALID_REQUEST para la franja duplicada
// — mismo error 400 (uq_exception vs uq_quota), mensaje distinto según qué
// mutación lo disparó. `extraCodeMessages` centraliza esa variación por
// llamador sin duplicar el resto de `reportSchedulingError`.
function reportSchedulingError(
  toast: ReturnType<typeof useToast>,
  error: unknown,
  extraCodeMessages?: Partial<Record<string, string>>,
) {
  const conflict = quotaConflictMessage(error);
  if (conflict) {
    toast.show(conflict, "error");
    return;
  }
  const codeMessages: Partial<Record<string, string>> = {
    ...(error instanceof ApiError && error.code === "CRM_UNAVAILABLE"
      ? { CRM_UNAVAILABLE: "No se pudo conectar con el sistema de talleres" }
      : undefined),
    ...extraCodeMessages,
  };
  reportError(toast, error, codeMessages);
}

// PLAN-009 §7.5 (RF-AGE-06, admin.jsx:61-66): "CRM · hace 12 min" cuando la
// sincronización fue reciente, "CRM · <fecha corta>" cuando no; "Manual ·
// <fecha corta>" para edición manual. Sin `syncedAt`, el prefijo se conserva
// y el resto es "Nunca sincronizado".
function formatSyncedAt(
  fromCrm: boolean,
  syncedAt: string | null | undefined,
): string {
  const prefix = fromCrm ? "CRM" : "Manual";
  if (!syncedAt) return `${prefix} · Nunca sincronizado`;
  const date = new Date(syncedAt);
  if (Number.isNaN(date.getTime())) return `${prefix} · Nunca sincronizado`;
  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMinutes >= 0 && diffMinutes < 60) {
    return `${prefix} · hace ${String(diffMinutes)} min`;
  }
  return `${prefix} · ${formatShortDate(syncedAt)}`;
}

// PLAN-009 §7.7.b: modal mínimo sobre `Modal` de `PortalPrimitives`, un solo
// campo Hora (`HH:MM`), día implícito (el del selector «Cupos · <día>»).
const SLOT_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

// QA-MTZ-002 #13 (decisión humana 2026-09-23, qa-bug-matrix-2026-09.md §7):
// la franja se rechaza en API y web si cae fuera de `[apertura, cierre)` o
// en un día cerrado, contra el horario GUARDADO (no el borrador de las
// tarjetas). Mismo texto que la API (`slotTime`, 400 INVALID_REQUEST). Los
// nombres en minúscula van indexados por el mismo `day` que `DAY_LABELS`.
const DAY_NAMES_SINGULAR: Record<number, string> = {
  0: "domingo",
  1: "lunes",
  2: "martes",
  3: "miércoles",
  4: "jueves",
  5: "viernes",
  6: "sábado",
};
const DAY_NAMES_PLURAL: Record<number, string> = {
  0: "domingos",
  1: "lunes",
  2: "martes",
  3: "miércoles",
  4: "jueves",
  5: "viernes",
  6: "sábados",
};

/** `null` si la franja cabe en el horario guardado del día; si no, el
 * mensaje que se muestra bajo el campo Hora. Sin horario cargado (`hours`
 * `undefined`) no se valida: decide la API. Un día sin fila se trata como
 * cerrado, igual que `formatHoursEntry`. */
function slotOutsideHoursMessage(
  day: number,
  slotTime: string,
  hours: readonly SchedulingHoursDay[] | undefined,
): string | null {
  if (!hours) return null;
  const entry = hours.find((item) => item.day === day);
  if (!entry || entry.closed || !entry.opensAt || !entry.closesAt) {
    return `El taller no atiende los ${DAY_NAMES_PLURAL[day] ?? ""}.`;
  }
  const opensAt = entry.opensAt.slice(0, 5);
  const closesAt = entry.closesAt.slice(0, 5);
  if (slotTime >= opensAt && slotTime < closesAt) return null;
  return `La franja debe estar dentro del horario de atención del ${
    DAY_NAMES_SINGULAR[day] ?? ""
  } (${opensAt}–${closesAt}).`;
}

function AddSlotModal({
  day,
  dayLabel,
  savedHours,
  serverError,
  onClose,
  onSubmit,
  submitting,
}: {
  day: number;
  dayLabel: string;
  savedHours: readonly SchedulingHoursDay[] | undefined;
  serverError: string | undefined;
  onClose: () => void;
  onSubmit: (slotTime: string) => void;
  submitting: boolean;
}) {
  const [slotTime, setSlotTime] = useState("08:00");
  const [error, setError] = useState<string | undefined>(undefined);
  const submit = () => {
    const trimmed = slotTime.trim();
    if (!SLOT_TIME_RE.test(trimmed)) {
      setError("Usa el formato HH:MM");
      return;
    }
    const outside = slotOutsideHoursMessage(day, trimmed, savedHours);
    if (outside !== null) {
      setError(outside);
      return;
    }
    setError(undefined);
    onSubmit(trimmed);
  };
  // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: pasa a las tres regiones
  // del `Modal` y a `PortalButton` en las dos acciones. Este diálogo NO usa
  // `EditorModal`: no tiene `<form>` ni `react-hook-form` detrás — el único
  // campo se valida a mano contra `SLOT_TIME_RE` en `submit`, así que envolver
  // todo en un `<form>` solo para tener un `type="submit"` agregaría un
  // camino de envío (Enter en el campo) que hoy no existe.
  return (
    <Modal
      label="Añadir horario"
      width={360}
      onClose={onClose}
      // El diálogo no tenía ningún título VISIBLE: el texto "Añadir horario"
      // solo existía como nombre accesible (`label`). Se promueve a
      // encabezado para que tenga las tres regiones como el resto —es el
      // mismo texto, no uno nuevo— con la tipografía del encabezado de
      // `ImpactConfirmDialog`, el diálogo chico ya consolidado.
      // Punto #21 (revisión en vivo 2026-09-22): el encabezado común de
      // todos los diálogos (eyebrow + `h3.display`) reemplaza aquel título.
      header={<DialogHeader eyebrow="Agendamiento" title="Añadir horario" />}
      footer={
        <DialogActions onCancel={onClose}>
          <PortalButton size="small" disabled={submitting} onClick={submit}>
            {submitting ? "Añadiendo…" : "Añadir"}
          </PortalButton>
        </DialogActions>
      }
    >
      <Field label={`Hora · ${dayLabel}`} error={error ?? serverError}>
        <input
          value={slotTime}
          onChange={(event) => setSlotTime(event.target.value)}
          placeholder="HH:MM"
          style={inputStyleDate}
        />
      </Field>
    </Modal>
  );
}

function ExceptionRow({
  exception,
  canReopen,
  onReopen,
}: {
  exception: SchedulingException;
  canReopen: boolean;
  onReopen: (exception: SchedulingException) => void;
}) {
  // PLAN-009 §7.3 (P8-3): «Registrada por» muestra el NOMBRE, nunca el uuid
  // `createdBy`; sin nombre (usuario borrado) → "— · <fecha corta>".
  const registeredBy = `${exception.createdByName ?? "—"} · ${formatShortDate(
    exception.createdAt,
  )}`;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 2.2fr 1.4fr 0.5fr",
        gap: 0,
        alignItems: "center",
        padding: "15px 24px",
        borderBottom: `1px solid ${tokens.surface.line}`,
      }}
    >
      <span
        style={{
          font: `600 13px ${tokens.font.ui}`,
          color: tokens.text.primary,
        }}
      >
        {formatShortDate(exception.onDate)}
      </span>
      <span>
        <span className={`stat ${exception.slotTime ? "stat-gold" : ""}`}>
          {exception.slotTime ?? "Día completo"}
        </span>
      </span>
      <span
        style={{
          font: `400 13px ${tokens.font.ui}`,
          color: tokens.text.secondary,
        }}
      >
        {exception.reason}
      </span>
      <span
        style={{
          font: `400 12px ${tokens.font.ui}`,
          color: tokens.text.tertiary,
        }}
      >
        {registeredBy}
      </span>
      <span style={{ textAlign: "right" }}>
        {canReopen ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ width: "auto" }}
            onClick={() => onReopen(exception)}
          >
            Reabrir
          </button>
        ) : null}
      </span>
    </div>
  );
}

export function SchedulingScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const canEdit = hasPermission("content", "canEdit");

  const workshops = useWorkshops();
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!selectedWorkshopId && workshops.data && workshops.data.length > 0) {
      setSelectedWorkshopId(workshops.data[0]?.id ?? null);
    }
  }, [selectedWorkshopId, workshops.data]);

  const [selectedDay, setSelectedDay] = useState(1);
  const hours = useWorkshopHours(selectedWorkshopId ?? undefined);
  const quotas = useWorkshopQuotas(
    selectedWorkshopId ?? undefined,
    selectedDay,
  );
  const exceptions = useSchedulingExceptions(selectedWorkshopId ?? undefined);
  const mutations = useSchedulingMutations(
    selectedWorkshopId ?? undefined,
    selectedDay,
  );

  const [hoursDraft, setHoursDraft] = useState<HoursDraft>({});
  const [hoursErrors, setHoursErrors] = useState<HoursErrors>({});
  useEffect(() => {
    if (!hours.data) return;
    const draft: HoursDraft = {};
    for (const day of DAY_ORDER) {
      const entry = hours.data.find((d) => d.day === day);
      draft[day] = formatHoursEntry(entry);
    }
    setHoursDraft(draft);
    setHoursErrors({});
  }, [hours.data]);

  const [quotasDraft, setQuotasDraft] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!quotas.data) return;
    const draft: Record<string, boolean> = {};
    for (const slot of quotas.data.slots) draft[slot.id] = slot.active;
    setQuotasDraft(draft);
  }, [quotas.data]);

  const [showAddSlot, setShowAddSlot] = useState(false);
  // Mismo patrón que `exceptionFieldErrors`: un rechazo que la API
  // identifica por campo (`slotTime`) se pinta bajo ese campo del modal.
  const [addSlotFieldErrors, setAddSlotFieldErrors] = useState<{
    slotTime?: string | undefined;
  }>({});
  const [showAddException, setShowAddException] = useState(false);
  const [exceptionValues, setExceptionValues] = useState<ExceptionFormValues>({
    onDate: "",
    slotTime: "",
    reason: "",
  });
  const [exceptionFieldErrors, setExceptionFieldErrors] =
    useState<ExceptionFieldErrors>({});

  const workshop = workshops.data?.find((w) => w.id === selectedWorkshopId);
  const activeQuotaCount = Object.values(quotasDraft).filter(Boolean).length;
  const totalQuotaCount = quotas.data?.slots.length ?? 0;

  const saveAvailability = () => {
    if (!selectedWorkshopId) return;
    // PLAN-009 §7.2 (P8-2): parseo de las 7 tarjetas antes de tocar el
    // servidor — un "HH:MM–HH:MM" inválido o un cierre ≤ apertura nunca
    // llega al backend como INVALID_REQUEST; se marca como error de campo
    // bajo la tarjeta correspondiente y se aborta el guardado completo.
    const parsed: Record<number, HoursParseResult> = {};
    const errors: HoursErrors = {};
    let hasError = false;
    for (let day = 0; day < 7; day += 1) {
      const result = parseHoursEntry(hoursDraft[day] ?? "Cerrado");
      parsed[day] = result;
      if (!result.ok) {
        errors[day] = result.message;
        hasError = true;
      }
    }
    setHoursErrors(errors);
    if (hasError) return;

    const hoursInput: SchedulingHoursDayInput[] = Array.from(
      { length: 7 },
      (_, day) => {
        const result = parsed[day];
        if (result && result.ok) {
          return {
            day,
            opensAt: result.opensAt,
            closesAt: result.closesAt,
            closed: result.closed,
          };
        }
        return { day, opensAt: null, closesAt: null, closed: true };
      },
    );

    mutations.saveHours.mutate(hoursInput, {
      onError: (error) => reportSchedulingError(toast, error),
      onSuccess: () => {
        const updates = quotas.data
          ? quotas.data.slots
              .filter((slot) => quotasDraft[slot.id] !== slot.active)
              .map((slot) => ({ id: slot.id, active: quotasDraft[slot.id]! }))
          : [];
        if (updates.length === 0) {
          toast.show("Disponibilidad guardada");
          return;
        }
        mutations.saveQuotas.mutate(
          { updates },
          {
            onSuccess: () => toast.show("Disponibilidad guardada"),
            onError: (error) => {
              reportSchedulingError(toast, error);
              // El lote se aplica hasta el primer rechazo: la pantalla relee
              // el estado real de los cupos en vez de confiar en el borrador
              // local, que puede haber quedado a mitad de camino.
              void quotas.refetch();
            },
          },
        );
      },
    });
  };

  const submitAddSlot = (slotTime: string) => {
    setAddSlotFieldErrors({});
    mutations.addQuotaSlot.mutate(
      { day: selectedDay, slotTime },
      {
        onSuccess: () => setShowAddSlot(false),
        onError: (error) => {
          // QA-MTZ-002 #13: la franja fuera de horario llega como
          // INVALID_REQUEST con campo `slotTime` — se muestra bajo Hora y
          // gana sobre el mapeo de duplicado (misma razón que
          // `apiFieldErrorMessage` documenta arriba).
          const slotMessage = apiFieldErrorMessage(error, "slotTime");
          if (slotMessage !== null) {
            setAddSlotFieldErrors({ slotTime: slotMessage });
            markErrorHandled(error);
            return;
          }
          const codeMessages =
            error instanceof ApiError && error.code === "INVALID_REQUEST"
              ? {
                  INVALID_REQUEST:
                    "Ya existe una franja configurada para ese día y hora",
                }
              : undefined;
          reportError(toast, error, codeMessages);
        },
      },
    );
  };

  const submitException = () => {
    const parsed = exceptionFormSchema.safeParse(exceptionValues);
    if (!parsed.success) {
      // PLAN-009 §7.6: cada `issue.path[0]` de zod va a SU slot (onDate,
      // slotTime, reason) — antes, un único mensaje siempre caía bajo Motivo
      // aunque el campo vacío fuera Fecha.
      const errors: ExceptionFieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === "onDate" || field === "slotTime" || field === "reason") {
          errors[field] ??= issue.message;
        }
      }
      setExceptionFieldErrors(errors);
      return;
    }
    if (isDuplicateException(exceptions.data, parsed.data)) {
      setExceptionFieldErrors({ slotTime: DUPLICATE_EXCEPTION_MESSAGE });
      return;
    }
    setExceptionFieldErrors({});
    mutations.createException.mutate(
      {
        onDate: parsed.data.onDate,
        slotTime: parsed.data.slotTime ? parsed.data.slotTime : null,
        reason: parsed.data.reason,
      },
      {
        onSuccess: (created) => {
          setShowAddException(false);
          setExceptionValues({ onDate: "", slotTime: "", reason: "" });
          setExceptionFieldErrors({});
          toast.show(`Fecha cerrada · ${formatShortDate(created.onDate)}`);
        },
        onError: (error) => {
          // Un rechazo que la API identifica por campo (hoy `reason`; la
          // fecha o el alcance si llegara a nombrarlos) va bajo ese campo.
          const fieldErrors: ExceptionFieldErrors = {};
          for (const field of EXCEPTION_FIELDS) {
            const message = apiFieldErrorMessage(error, field);
            if (message !== null) fieldErrors[field] = message;
          }
          if (Object.keys(fieldErrors).length > 0) {
            setExceptionFieldErrors(fieldErrors);
            markErrorHandled(error);
            return;
          }
          reportSchedulingError(toast, error, {
            INVALID_REQUEST: DUPLICATE_EXCEPTION_MESSAGE,
          });
        },
      },
    );
  };

  const reopenException = (exception: SchedulingException) => {
    mutations.reopenException.mutate(exception.id, {
      onSuccess: () =>
        toast.show(`Fecha reabierta · ${formatShortDate(exception.onDate)}`),
      onError: (error) => reportSchedulingError(toast, error),
    });
  };

  const runSync = () => {
    if (!selectedWorkshopId) return;
    mutations.sync.mutate(undefined, {
      onError: (error) => reportSchedulingError(toast, error),
    });
  };

  return (
    <div>
      {/* Cabecera de pantalla — PLAN-009 §7.7.a: título y subtítulo EXACTOS
        del shell del prototipo (admin.jsx:3903 NAV "agenda"/3909), no la
        redacción libre de la corrida anterior. */}
      <header style={{ marginBottom: 0 }}>
        <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
          Agendamiento
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "2px 0 26px",
            fontSize: 13,
          }}
        >
          Talleres, horarios y cupos disponibles para agendar.
        </p>
      </header>
      <div
        style={{
          ...cardStyle(),
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 18,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 13,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: tokens.surface.surface2,
            border: `1px solid ${tokens.surface.line}`,
            color: tokens.text.tertiary,
            flex: "none",
          }}
        >
          <Icon name="link" size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              font: `600 14px ${tokens.font.ui}`,
              color: tokens.text.primary,
            }}
          >
            Conexión con el CRM
          </div>
          <div
            style={{
              font: `400 12px ${tokens.font.ui}`,
              color: tokens.text.tertiary,
              marginTop: 3,
            }}
          >
            Talleres, horarios y disponibilidad se leen del CRM. También puedes
            editarlos a mano aquí — la edición manual gana hasta la próxima
            sincronización.
          </div>
        </div>
        <span className="stat stat-gold">API conectada</span>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ width: "auto", padding: "11px 20px" }}
          onClick={runSync}
          disabled={mutations.sync.isPending || !selectedWorkshopId}
        >
          {mutations.sync.isPending ? "Sincronizando…" : "Sincronizar ahora"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "300px 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        <div style={cardStyle(14)}>
          <div className="label" style={{ padding: "6px 8px 12px" }}>
            Talleres
          </div>
          {workshops.isLoading ? (
            <div style={{ padding: 12, color: tokens.text.tertiary }}>
              Cargando…
            </div>
          ) : (
            workshops.data?.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => setSelectedWorkshopId(w.id)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  padding: "13px 14px",
                  borderRadius: 12,
                  marginBottom: 6,
                  background:
                    w.id === selectedWorkshopId
                      ? tokens.surface.surface2
                      : "transparent",
                  border: `1px solid ${
                    w.id === selectedWorkshopId
                      ? tokens.surface.line2
                      : "transparent"
                  }`,
                }}
              >
                <div
                  style={{
                    font: `600 13px ${tokens.font.ui}`,
                    color: tokens.text.primary,
                  }}
                >
                  {w.name}
                </div>
                <div
                  style={{
                    font: `400 11px ${tokens.font.ui}`,
                    color: tokens.text.tertiary,
                    marginTop: 3,
                  }}
                >
                  {w.city} · {w.bays ?? 0} bahías
                </div>
                <div
                  style={{
                    font: `400 10.5px ${tokens.font.ui}`,
                    color: tokens.text.disabled,
                    marginTop: 4,
                  }}
                >
                  {formatSyncedAt(w.fromCrm, w.syncedAt)}
                </div>
              </button>
            ))
          )}
        </div>

        <div>
          <div style={{ ...cardStyle(), marginBottom: 20 }}>
            <h3 className="display" style={{ fontSize: 22, margin: "0 0 4px" }}>
              {workshop?.name ?? "—"}
            </h3>
            <div
              style={{
                font: `400 12px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                marginBottom: 20,
              }}
            >
              Horario de atención por día.
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))",
                gap: 10,
              }}
            >
              {DAY_ORDER.map((day) => {
                const label = DAY_LABELS[day] ?? "";
                const value = hoursDraft[day] ?? "Cerrado";
                const error = hoursErrors[day];
                return (
                  <div
                    key={day}
                    role="group"
                    aria-label={label}
                    style={{
                      border: `1px solid ${
                        selectedDay === day
                          ? tokens.gold.line
                          : tokens.surface.line
                      }`,
                      borderRadius: 12,
                      padding: 12,
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedDay(day)}
                  >
                    <div className="label" style={{ marginBottom: 8 }}>
                      {label.slice(0, 3)}
                    </div>
                    <input
                      value={value}
                      disabled={!canEdit}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        const next = event.target.value;
                        setHoursDraft((prev) => ({ ...prev, [day]: next }));
                        setHoursErrors((prev) => ({
                          ...prev,
                          [day]: undefined,
                        }));
                      }}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "8px 9px",
                        fontSize: 11.5,
                        textAlign: "center",
                        borderRadius: 11,
                        border: `1px solid ${tokens.surface.line2}`,
                        background: "rgba(255,255,255,0.03)",
                        color: tokens.text.primary,
                        font: `400 11.5px ${tokens.font.ui}`,
                      }}
                    />
                    {error ? <FieldError message={error} /> : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={cardStyle()}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 18,
              }}
            >
              <div>
                <h3 className="display" style={{ fontSize: 22, margin: 0 }}>
                  Cupos · {DAY_LABELS[selectedDay]}
                </h3>
                <div
                  style={{
                    font: `400 12px ${tokens.font.ui}`,
                    color: tokens.text.tertiary,
                    marginTop: 4,
                  }}
                >
                  Los horarios activos son los que el miembro ve al agendar.
                </div>
              </div>
              <Can module="content" action="canCreate">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setAddSlotFieldErrors({});
                    setShowAddSlot(true);
                  }}
                >
                  <Icon name="plus" size={14} /> Añadir horario
                </button>
              </Can>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {quotas.isLoading ? (
                <span style={{ color: tokens.text.tertiary }}>Cargando…</span>
              ) : (
                quotas.data?.slots.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={!canEdit}
                    onClick={() =>
                      setQuotasDraft((prev) => ({
                        ...prev,
                        [slot.id]: !prev[slot.id],
                      }))
                    }
                    className={`chip${quotasDraft[slot.id] ? " on" : ""}`}
                    style={{ padding: "11px 18px", fontSize: 13 }}
                  >
                    {slot.slotTime}
                  </button>
                ))
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 22,
              }}
            >
              <Can module="content" action="canEdit">
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: "auto", padding: "12px 22px" }}
                  onClick={saveAvailability}
                  disabled={
                    mutations.saveHours.isPending ||
                    mutations.saveQuotas.isPending ||
                    !selectedWorkshopId
                  }
                >
                  {mutations.saveHours.isPending ||
                  mutations.saveQuotas.isPending
                    ? "Guardando…"
                    : "Guardar disponibilidad"}
                </button>
              </Can>
              <span
                style={{
                  font: `400 11.5px ${tokens.font.ui}`,
                  color: tokens.text.disabled,
                }}
              >
                {activeQuotaCount} de {totalQuotaCount} horarios activos
              </span>
            </div>
          </div>

          <div style={{ ...cardStyle(0), marginTop: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "22px 24px 18px",
              }}
            >
              <div>
                <h3 className="display" style={{ fontSize: 22, margin: 0 }}>
                  Excepciones por fecha
                </h3>
                <div
                  style={{
                    font: `400 12px ${tokens.font.ui}`,
                    color: tokens.text.tertiary,
                    marginTop: 4,
                  }}
                >
                  Un feriado o una semana de inventario cierran el taller en una
                  fecha concreta. La excepción manda sobre el horario semanal.
                </div>
              </div>
              <Can module="content" action="canCreate">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ width: "auto", whiteSpace: "nowrap" }}
                  onClick={() => setShowAddException((v) => !v)}
                >
                  <Icon name="plus" size={14} /> Añadir excepción
                </button>
              </Can>
            </div>

            {showAddException ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "150px 150px 1fr auto",
                  gap: 12,
                  alignItems: "end",
                  padding: "0 24px 20px",
                }}
              >
                <Field label="Fecha" error={exceptionFieldErrors.onDate}>
                  <input
                    type="date"
                    value={exceptionValues.onDate}
                    onChange={(event) =>
                      setExceptionValues((prev) => ({
                        ...prev,
                        onDate: event.target.value,
                      }))
                    }
                    style={{ ...inputStyleDate }}
                  />
                </Field>
                <Field label="Alcance" error={exceptionFieldErrors.slotTime}>
                  <select
                    value={exceptionValues.slotTime}
                    onChange={(event) =>
                      setExceptionValues((prev) => ({
                        ...prev,
                        slotTime: event.target.value,
                      }))
                    }
                    style={{ ...inputStyleDate }}
                  >
                    <option value="">Día completo</option>
                    {quotas.data?.slots.map((slot) => (
                      <option key={slot.id} value={slot.slotTime}>
                        {slot.slotTime}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Motivo" error={exceptionFieldErrors.reason}>
                  <input
                    value={exceptionValues.reason}
                    onChange={(event) =>
                      setExceptionValues((prev) => ({
                        ...prev,
                        reason: event.target.value,
                      }))
                    }
                    placeholder="Feriado, inventario, capacitación…"
                    style={{ ...inputStyleDate }}
                  />
                </Field>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: "auto", padding: "11px 20px" }}
                  disabled={mutations.createException.isPending}
                  onClick={submitException}
                >
                  Cerrar fecha
                </button>
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 2.2fr 1.4fr 0.5fr",
                gap: 0,
                padding: "0 24px 10px",
                borderBottom: `1px solid ${tokens.surface.line}`,
              }}
            >
              {["Fecha", "Alcance", "Motivo", "Registrada por"].map((h) => (
                <span key={h} className="label">
                  {h}
                </span>
              ))}
              <span className="label" style={{ textAlign: "right" }}>
                Acciones
              </span>
            </div>

            {exceptions.isLoading ? (
              <div style={{ padding: 24, color: tokens.text.tertiary }}>
                Cargando…
              </div>
            ) : !exceptions.data || exceptions.data.length === 0 ? (
              <div
                style={{
                  padding: "30px 24px",
                  font: `400 13px ${tokens.font.ui}`,
                  color: tokens.text.disabled,
                  textAlign: "center",
                }}
              >
                Sin excepciones registradas. El taller atiende según su horario
                semanal.
              </div>
            ) : (
              exceptions.data.map((exception) => (
                <ExceptionRow
                  key={exception.id}
                  exception={exception}
                  canReopen={canEdit}
                  onReopen={reopenException}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {showAddSlot ? (
        <AddSlotModal
          day={selectedDay}
          dayLabel={DAY_LABELS[selectedDay] ?? ""}
          savedHours={hours.data}
          serverError={addSlotFieldErrors.slotTime}
          onClose={() => setShowAddSlot(false)}
          onSubmit={submitAddSlot}
          submitting={mutations.addQuotaSlot.isPending}
        />
      ) : null}
    </div>
  );
}

// Estilo compartido por los campos del formulario de excepción
// (fecha/alcance/motivo) y por el input "Hora" de `AddSlotModal`: mismo valor
// que `inputStyle` de `@portal/common/FormField`, definido acá porque esos
// campos usan `padding: "10px 12px"` en vez del `"11px 13px"` por defecto
// (línea heredada del diseño previo de esta pantalla).
const inputStyleDate: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 11,
  border: `1px solid ${tokens.surface.line2}`,
  background: "rgba(255,255,255,0.03)",
  color: tokens.text.primary,
  font: `400 13.5px ${tokens.font.ui}`,
};
